import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getCodeVelocityList,
  getTeamCollaborationList,
  getPrChartsGraphql,
  goalsGenericGraphql,
  teamStatsGraphql,
  getInProgress,
  doraChartTeamStatsGraphql,
  codeStatsGraphql,
  getTeamComparisonData,
  getIssueBySprintGraphql,
} from './query';

export const QUERY_KEYS = {
  teamList: 'teamList',
  contributorsList: 'contributorsList',
  codeVelocityList: 'codeVelocityList',
  sprintVelocityList: 'sprintVelocityList',
  teamCollaborationList: 'teamCollaborationList',
  prChartsGraphql: 'prChartsGraphql',
  goalsMetric: 'goalsMetric',
  inProgress: 'inProgress',
  teamStats: 'teamStats',
  doraChart: 'doraChart',
  codeStats: 'codeStats',
  teamComparison: 'teamComparison',
  sprintAssignee: 'sprintAssignee',
};

export const usePrChartsGraphql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  metric: string,
  teamId: string,
  project: string,
  searchTerm: string,
  author: string,
  reviewer: string,
  pageNumber: number,
  pageSize: number,
  timeZone?: string
) => {
  return useQuery(
    [
      QUERY_KEYS.prChartsGraphql,
      startDate,
      endDate,
      sprintId,
      metric,
      teamId,
      project,
      searchTerm,
      author,
      reviewer,
      pageNumber,
      pageSize,
      timeZone,
    ],
    () =>
      getPrChartsGraphql(
        startDate,
        endDate,
        sprintId,
        metric,
        teamId,
        project,
        searchTerm,
        author,
        reviewer,
        pageNumber,
        pageSize,
        timeZone
      ),
    {
      keepPreviousData: true,
      select: (data) => data.IndividualCollaborationDetail,
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useFetchInProgressGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  project: string,
  searchTerm: string,
  reviewer: string,
  author: string,
  type: string,
  status: string,
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.inProgress,
      startDate,
      pageNumber,
      pageSize,
      endDate,
      sprintId,
      teamId,
    ],
    () =>
      getInProgress(
        startDate,
        endDate,
        sprintId,
        teamId,
        project,
        searchTerm,
        reviewer,
        author,
        type,
        status,
        pageNumber,
        pageSize
      ),
    {
      keepPreviousData: true,
      select: (data) => data.InProgressItems,
      enabled: !!teamId && !!startDate && !!endDate,
    }
  );
};

export const useFetchTeamCollaborationGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.teamCollaborationList,
      startDate,
      endDate,
      sprintId,
      teamId,
      pageNumber,
      pageSize,
    ],
    () =>
      getTeamCollaborationList(
        startDate,
        endDate,
        sprintId,
        teamId,
        pageNumber,
        pageSize
      ),
    {
      keepPreviousData: true,
      select: (data) => data,
      enabled: !!teamId && !!startDate && !!endDate,
    }
  );
};

export const useFetchCodeVelocityGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  pageNumber: number,
  pageSize: number,
  project: string,
  searchTerm: string
) => {
  return useQuery(
    [
      QUERY_KEYS.codeVelocityList,
      startDate,
      endDate,
      sprintId,
      teamId,
      pageNumber,
      pageSize,
    ],
    () =>
      getCodeVelocityList(
        startDate,
        endDate,
        sprintId,
        teamId,
        pageNumber,
        pageSize,
        project,
        searchTerm
      ),
    {
      keepPreviousData: true,
      select: (data) => data.RepositoryActivitiesForDate,
      enabled: !!teamId && !!startDate && !!endDate,
    }
  );
};

export const useGoalsChartGql = (
  metric: string,
  startDate: string,
  endDate: string,
  sprintId: string,
  selectedTeam: string
) => {
  return useQuery(
    [
      QUERY_KEYS.goalsMetric,
      metric,
      startDate,
      endDate,
      sprintId,
      selectedTeam,
    ],
    () =>
      goalsGenericGraphql(metric, startDate, endDate, sprintId, selectedTeam),
    {
      select: (data) => ({
        ...data.GoalMetricsByType,
        data: JSON.parse(data.GoalMetricsByType.data),
      }),

      enabled: !!metric && !!selectedTeam && !!startDate && !!endDate,
    }
  );
};

export const useTeamStatGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  selectedTeam: string,
  type: string
) => {
  return useQuery(
    [QUERY_KEYS.teamStats, startDate, endDate, sprintId, selectedTeam, type],
    () => teamStatsGraphql(startDate, endDate, sprintId, selectedTeam, type),
    {
      select: (data) => data?.TeamStats,
      enabled: !!selectedTeam && !!startDate && !!endDate,
    }
  );
};

export const useDoraChartTeamStatGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  selectedTeam: string
) => {
  return useQuery(
    [QUERY_KEYS.doraChart, startDate, endDate, sprintId, selectedTeam],
    () => doraChartTeamStatsGraphql(startDate, endDate, sprintId, selectedTeam),
    {
      select: (data) => data?.TeamStats,
      enabled: !!selectedTeam && !!startDate && !!endDate,
    }
  );
};

export const useCodeStatsGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  selectedTeam: string
) => {
  return useQuery(
    [QUERY_KEYS.codeStats, startDate, endDate, sprintId, selectedTeam],
    () => codeStatsGraphql(startDate, endDate, sprintId, selectedTeam),
    {
      select: (data) => data?.TeamStats,
      enabled: !!selectedTeam && !!startDate && !!endDate,
    }
  );
};

export const useTeamComparisonGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teams: string[],
  metric: string,
  isPerMemberMetric: boolean
) => {
  return useQuery(
    [
      QUERY_KEYS.teamComparison,
      startDate,
      endDate,
      sprintId,
      teams,
      metric,
      isPerMemberMetric,
    ],
    () =>
      getTeamComparisonData(
        startDate,
        endDate,
        sprintId,
        teams,
        metric,
        isPerMemberMetric
      ),
    {
      select: (data) => data?.TeamComparisonData,
      keepPreviousData: true,
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useIssueBySprintGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  author: string,
  type: string,
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.sprintAssignee,
      startDate,
      endDate,
      sprintId,
      teamId,
      author,
      type,
      pageNumber,
      pageSize,
    ],
    () =>
      getIssueBySprintGraphql(
        startDate,
        endDate,
        sprintId,
        teamId,
        author,
        type,
        pageNumber,
        pageSize
      ),
    {
      select: (data) => data.IssueDetailsBySprint,
      enabled: !!sprintId,
    }
  );
};
